

import { ExpandMore as ChevronDown, ChevronRight } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContentDiff } from '@/lib/text-versioning';
import { EnrichedEntity } from '@/lib/types';


/**
 * Type guard for ContentDiff
 */
function isContentDiff(item: unknown): item is ContentDiff {
  return !!item && typeof item === 'object' && 'patch' in item && 'ts' in item;
}

/**
 * Type guard for EnrichedEntity
 */
function isEnrichedEntity(item: unknown): item is EnrichedEntity {
  return !!item && typeof item === 'object' && 'entityType' in item && 'id' in item;
}

type GroupItem = EnrichedEntity | ContentDiff;


interface EntityGroupProps<T extends GroupItem> {
  title: string;
  items: T[];
  icon: React.ReactNode;
  onSelect: (item: T, event: React.MouseEvent) => void;
  isHistory?: boolean;
  onDragStart?: (item: EnrichedEntity, event: React.DragEvent) => void;
}


export function EntityGroup<T extends GroupItem>({
  title,
  items,
  icon,
  onSelect,
  isHistory,
  onDragStart
}: EntityGroupProps<T>) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  if (items.length === 0) return null;

  return (
    <Box>
      <ListItemButton
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          bgcolor: 'action.hover',
          py: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={title}
          primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 600 }}
        />
        <Chip label={items.length} size="small" sx={{ mr: 1, height: 20, fontSize: '0.7rem' }} />
        {isOpen ? <ChevronDown sx={{ fontSize: 16 }} /> : <ChevronRight sx={{ fontSize: 16 }} />}
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {items.map((item, index) => {
            if (isContentDiff(item) && isHistory) {
              const ts = item.ts;
              const timestamp = typeof ts === 'string'
                ? new Date(ts)
                : new Date();

              const timeStr = timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              const dateStr = timestamp.toLocaleDateString([], { month: 'short', day: 'numeric' });

              return (
                <ListItemButton
                  key={item.seq || index}
                  sx={{ pl: 4, py: 0.75 }}
                  onClick={(event) => onSelect(item, event)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {timeStr}
                        </Typography>
                        <Chip
                          label={item.isAutomatic ? t('editor.auto') : t('editor.manual')}
                          size="small"
                          color={item.isAutomatic ? 'default' : 'primary'}
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                        />
                      </Box>
                    }
                    secondary={`${dateStr} • ${item.size || 0} ${t('editor.bytes')}`}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      sx: { color: 'text.secondary' },
                    }}
                  />
                </ListItemButton>
              );
            }

            if (isEnrichedEntity(item)) {
              const id = item.id;
              const name = (item.name || (item as { title?: string }).title) || t('editor.unknown');
              const subtext = (item.shortDescription || (item as { type?: string }).type) || '';



              return (
                <ListItemButton
                  key={id}
                  sx={{
                    pl: 4,
                    py: 0.5,
                    cursor: onDragStart ? 'grab' : 'pointer',
                    '&:active': onDragStart ? { cursor: 'grabbing' } : {},
                  }}
                  onClick={(event) => onSelect(item, event)}
                  draggable={!!onDragStart}
                  onDragStart={(e) => {
                    onDragStart?.(item, e);
                  }}

                >
                  <ListItemText
                    primary={name}
                    secondary={subtext}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      noWrap: true,
                      sx: { display: 'block' },
                    }}
                  />
                </ListItemButton>
              );
            }

            return null;
          })}
        </List>
      </Collapse>
    </Box>
  );
}
